import { CustomFieldBuilder } from "@/backend/access/customFields/customFieldBuilder";
import { isEmpty } from "@/backend/helpers/utils";
import { CustomField } from "@/backend/models/customFields/customField";
import { I18n } from "@/i18n/I18n";
import { CUSTOM_FIELD_TYPES } from "@/types/customFieldTypes";
import { useState, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useParameterStore } from "@/frontend/store/ParameterStore";
import { FileSubjectTypes } from "@/types/fileTypes";
import { FileUploader } from "../files/FileUploader";
import { ButtonParent } from "../buttons/ButtonParent";
import { Rewind } from "@/frontend/SVGs/Rewind";
import { Select } from "../Select";

export function CustomFieldInput(props: { readOnly?: boolean, label?: string, customField: CustomField, isEditView?: boolean, inputConstraint?: string, hideFilePreview?: boolean, isInput?: boolean }) {
  const { readOnly, customField, label, isEditView, inputConstraint, hideFilePreview = false, isInput = true } = props;
  const updateCustomField = useParameterStore(state => state.updateCustomField);
  const handleUpdatedConsumers = useParameterStore(state => state.handleUpdatedParamsAndRefs);
  const displayValue = isEditView ? customField.defaultValue : customField.getValue();
  const [value, setValue] = useState(displayValue?.toString() || '');
  const [error, setError] = useState('');
  const [patternError, setPatternError] = useState(false);
  const textFieldTypes = [CUSTOM_FIELD_TYPES.String, CUSTOM_FIELD_TYPES.Integer, CUSTOM_FIELD_TYPES.Float];

  useEffect(() => {
    setValue(displayValue?.toString() || '');
  }, [displayValue]);


  const onChange = (newValue: string) => {
    setValue(newValue);
    setError('');
    setPatternError(false);
    if (customField.isRequired && newValue === '' && !isEditView) {
      setError(I18n.get('requiredField'));
    }

    if (newValue) {
      if (isEditView) {
        const newCustomField = new CustomFieldBuilder().build({ ...customField, defaultValue: newValue });
        if (!newCustomField.hasValidDefaultInput()) {
          setPatternError(true);
        }
      } else {
        const newCustomField = new CustomFieldBuilder().build({ ...customField, customValue: newValue });
        if (!newCustomField.hasValidCustomInput()) {
          setPatternError(true);
        }
      }
    }
  };

  const saveValue = async (newValue: string) => {
    onChange(newValue);
    if (error || patternError) return;
    const fieldName = isEditView ? 'defaultValue' : 'customValue';
    if (isEmpty(customField[fieldName]) && isEmpty(newValue)) return;
    if (isEditView && customField.defaultValue?.toString() === newValue) return;
    if (!isEditView && customField.asString() === newValue) return;
    void updateCustomField({ ...customField, [fieldName]: newValue });
  };

  return <div className="flex-fill" key={customField.id}>
    { label && <Form.Label className="col-form-label col-form-label-sm">{label} <small className='text-muted text-uppercase'>{`(${I18n.get(customField.fieldType)})`}</small></Form.Label> }
    { customField.fieldType === CUSTOM_FIELD_TYPES.Boolean &&
      <InputGroup>
        <Select label={I18n.get('value')} value={value || I18n.get('none')} disabled={readOnly} minWidth='400px'>
          <button className='dropdown-item' type='button' onClick={() => void saveValue('true')}>{I18n.get('true')}</button>
          <button className='dropdown-item' type='button' onClick={() => void saveValue('false')}>{I18n.get('false')}</button>
          <button className='dropdown-item' type='button' onClick={() => void saveValue('')}>{I18n.get('none')}</button>
        </Select>
        { error &&
          <Form.Control.Feedback type="invalid">
            { error }
          </Form.Control.Feedback>
        }
        <ResetToDefaultButton isEditView={isEditView} customField={customField} readOnly={readOnly} isInput={isInput} onClick={() => saveValue(customField.defaultValue?.toString() || '')} title={I18n.get('resetToDefault')} />
      </InputGroup>
    }
    { textFieldTypes.includes(customField.fieldType) &&
      <div className='d-flex'>
        <div className='flex-fill'>
          <InputGroup>
            <Form.Control
              size='sm'
              aria-label={I18n.get('enterField', { field: label || I18n.get('value') })}
              className={!isEditView && !customField.customValue ? 'text-secondary' : ''}
              value={value as string}
              isInvalid={!!error || patternError}
              type={'text'}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => void saveValue(value)}
              disabled={readOnly}
              id={customField.id}
              style={{ minWidth: '400px', maxWidth: '400px' }}
            />
            <Form.Control.Feedback type="invalid">
              { error }
            </Form.Control.Feedback>
            <ResetToDefaultButton isEditView={isEditView} customField={customField} readOnly={readOnly} isInput={isInput} onClick={() => saveValue(customField.defaultValue?.toString() || '')} title={I18n.get('resetToDefault')} />
          </InputGroup>
          {customField.fieldType === CUSTOM_FIELD_TYPES.Float &&
            <div className={patternError ? 'text-sm-danger' : 'text-sm-hint'}>
              {I18n.get('floatError')}
            </div>
          }
          {customField.fieldType === CUSTOM_FIELD_TYPES.Integer &&
            <div className={patternError ? 'text-sm-danger' : 'text-sm-hint'}>
              {I18n.get('integerError')}
            </div>
          }
        </div>
      </div>
    }
    { customField.fieldType === CUSTOM_FIELD_TYPES.MultilineText &&
      <div className='d-flex'>
        <div className='flex-fill'>
          <InputGroup>
            <Form.Control
              as='textarea'
              rows={6}
              size='sm'
              aria-label={I18n.get('enterField', { field: label || I18n.get('value') })}
              className={!isEditView && !customField.customValue ? 'text-secondary' : ''}
              value={value as string}
              isInvalid={!!error || patternError}
              onChange={(e) => onChange(e.target.value)}
              onBlur={() => void saveValue(value)}
              disabled={readOnly}
              id={customField.id}
              style={{ minWidth: '400px', maxWidth: '400px' }}
            />
            <Form.Control.Feedback type="invalid">
              { error }
            </Form.Control.Feedback>
            <ResetToDefaultButton isEditView={isEditView} customField={customField} readOnly={readOnly} isInput={isInput} onClick={() => saveValue(customField.defaultValue?.toString() || '')} title={I18n.get('resetToDefault')} />
          </InputGroup>
        </div>
      </div>
    }
    { customField.fieldType === CUSTOM_FIELD_TYPES.File && <FileUploader
      isInput={isInput}
      subjectId={customField.id}
      subjectType={FileSubjectTypes.CUSTOM_FIELD}
      refetchSubject={handleUpdatedConsumers}
      file={customField.file}
      readOnly={readOnly}
      inputConstraint={inputConstraint}
      hideFilePreview={hideFilePreview}
    />}
  </div>;
}

function ResetToDefaultButton(props: {
  isEditView?: boolean,
  customField: CustomField,
  readOnly?: boolean,
  isInput: boolean,
  onClick: () => Promise<void>,
  title: string
}) {
  const { isEditView, customField, readOnly, isInput, onClick, title } = props;
  if (!isEditView && !isEmpty(customField.defaultValue) && !readOnly && isInput) {
    return (
      <ButtonParent className='btn btn-sm btn-light border-0 rounded-start-0' onClick={() => void onClick()} title={title}>
        <Rewind />
      </ButtonParent>
    );
  }
  return null;
}
